import type { CostWindow } from '../types/optimise';

import { formatHHmmInTz, fromUnixTime, normalizeToUnixSeconds, SITE_TIMEZONE } from './dateUtils';

export type BatteryCommand = 'charge' | 'discharge' | 'hold';

export type RateType = 'cheap' | 'day' | 'peak';

export type BatteryActivityType = 'gridCharge' | 'solarCharge' | 'consumption' | 'export' | 'idle';

export const ACTIVITY_LABELS: Record<BatteryActivityType, string> = {
  gridCharge: 'Charge battery from grid',
  solarCharge: 'Charge from solar',
  consumption: 'Consume from battery',
  export: 'Export to grid',
  idle: 'Battery idle',
};

export interface BehaviourForecastRecord {
  target_datetime_utc: number; // Unix timestamp in seconds
  optimal_target_soc_original: number; // Target SOC percentage
  battery_update_command?: BatteryCommand;
}

export interface EnrichedForecastPoint {
  x: string; // Time (HH:mm)
  y: number; // SOC at the START of this interval (before the action runs)
  data: BehaviourForecastRecord;
  command: BatteryCommand;
  rateType: RateType;
  rate: number; // Rate in p/kWh
  startTime: string; // Command start time (HH:mm)
  endTime: string; // Command end time - next point's time (HH:mm)
  activityType: BatteryActivityType;
  _endSoc?: number; // Target SOC at the END of this interval (after the action completes); used for tooltip
  _previousSoc?: number; // Previous point's SOC, used for kWh calculation in historic tooltips
  _skipTooltip?: boolean; // True on boundary-echo copies that exist only for line continuity
}

export interface ChartSegment {
  activityType: BatteryActivityType;
  data: EnrichedForecastPoint[];
}

export const formatTimeFromUtcTimestamp = (timestampUtc: number, tz: string = SITE_TIMEZONE): string => {
  return formatHHmmInTz(tz, fromUnixTime(timestampUtc));
};

/**
 * Derives the battery activity type from the command and tariff rate type.
 *
 * Logic:
 * - charge + cheap rate  → Grid charge (optimiser charges from grid during cheap rates)
 * - charge + day/peak    → Solar charge (only economical to charge from solar at higher rates)
 * - discharge + peak     → Export to grid (selling stored energy when rates are highest)
 * - discharge + day/cheap → Battery usage (powering home from stored energy)
 * - hold (any rate)      → Battery idle
 *
 * @param command - The battery command type from the API
 * @param rateType - The tariff rate category for the time period
 * @returns The derived battery activity type
 */
export const deriveActivityType = (command: BatteryCommand, rateType: RateType): BatteryActivityType => {
  if (command === 'hold') return 'idle';

  if (command === 'charge') {
    return rateType === 'cheap' ? 'gridCharge' : 'solarCharge';
  }

  return rateType === 'peak' ? 'export' : 'consumption';
};

/**
 * Splits an array of enriched forecast points into contiguous segments
 * where each segment shares the same activity type.
 *
 * Adjacent segments share an overlap point (the last point of one segment
 * is duplicated as the first point of the next) to ensure visual line continuity.
 *
 * @param points - Array of enriched forecast points (must have activityType set)
 * @returns Array of chart segments, each with an activity type and data points
 *
 */
/**
 * Controls where the colour boundary sits when two adjacent points have different activities.
 *
 * - `'start'` (default) — for data where x = **start** of interval (future forecast).
 *   The outgoing segment is extended to the transition point; the new segment also starts
 *   there. Colour change aligns with the tick where the new activity begins.
 *
 * - `'prev'` — for data where x = **end** of interval (historic 24h data).
 *   The outgoing segment ends at the previous point; the new segment starts at
 *   [prevPoint, transitionPoint]. Colour change aligns with the tick where the
 *   previous interval ended (= start of the new activity's interval).
 */
export type SegmentBoundaryMode = 'start' | 'prev';

export const segmentByActivity = (
  points: EnrichedForecastPoint[],
  boundaryMode: SegmentBoundaryMode = 'start'
): ChartSegment[] => {
  if (!points?.length) return [];

  const segments: ChartSegment[] = [];
  let currentSegment: ChartSegment = {
    activityType: points[0].activityType,
    data: [points[0]],
  };

  for (let i = 1; i < points.length; i++) {
    const point = points[i];

    if (point.activityType === currentSegment.activityType) {
      currentSegment.data.push(point);
    } else if (boundaryMode === 'start') {
      // x = start-of-interval: extend outgoing to boundary point so the line
      // visually reaches the transition tick, then start the new segment there.
      // The copy appended to the outgoing segment is marked _skipTooltip so
      // Voronoi doesn't fire a duplicate label at this x position.
      currentSegment.data.push({ ...point, _skipTooltip: true });
      segments.push(currentSegment);
      currentSegment = { activityType: point.activityType, data: [point] };
    } else {
      // x = end-of-interval (historic): outgoing ends at the previous tick;
      // new segment starts with [prevPoint, transitionPoint] so the coloured
      // line covers the interval that actually had the new activity.
      // The prevPoint copy at the head of the new segment is marked _skipTooltip.
      const prevPoint = currentSegment.data[currentSegment.data.length - 1];
      segments.push(currentSegment);
      currentSegment = { activityType: point.activityType, data: [{ ...prevPoint, _skipTooltip: true }, point] };
    }
  }

  segments.push(currentSegment);
  return segments;
};

type SortedWindow = { window: CostWindow; startSeconds: number };

/**
 * Pre-processes cost windows into a sorted array ready for binary/linear lookup.
 * Call once per render; pass the result to findRateInSortedWindows per point.
 */
export const presortCostWindows = (costWindows: CostWindow[]): SortedWindow[] =>
  costWindows
    .map((w) => ({ window: w, startSeconds: normalizeToUnixSeconds(w?.start_timestamp_utc) }))
    .filter((w): w is SortedWindow => typeof w.startSeconds === 'number')
    .sort((a, b) => a.startSeconds - b.startSeconds);

const findRateInSortedWindows = (tsSeconds: number, sorted: SortedWindow[]): CostWindow | undefined => {
  for (let i = 0; i < sorted.length; i++) {
    const current = sorted[i];
    const next = sorted[i + 1];
    if (next) {
      if (tsSeconds >= current.startSeconds && tsSeconds < next.startSeconds) return current.window;
    } else {
      if (tsSeconds >= current.startSeconds) return current.window;
    }
  }
  return undefined;
};

export const findRateForTimestamp = (timestampUtc: number, costWindows: CostWindow[]): CostWindow | undefined => {
  if (!costWindows?.length) return undefined;
  const tsSeconds = normalizeToUnixSeconds(timestampUtc);
  if (tsSeconds === null) return undefined;
  const sorted = presortCostWindows(costWindows);
  if (sorted.length === 0) return undefined;
  return findRateInSortedWindows(tsSeconds, sorted);
};

/**
 * Categorises a rate value into cheap, day, or peak based on the rate value
 * relative to other rates in the cost windows
 *
 * @param rate - The rate value in p/kWh
 * @param costWindows - Array of cost windows to derive thresholds from
 * @returns The rate type category
 */
export const categoriseRate = (rate: number, costWindows: CostWindow[]): RateType => {
  if (!costWindows?.length) return 'day';

  const uniqueRates = [...new Set(costWindows.map((w) => w.rate))].sort((a, b) => a - b);

  if (uniqueRates.length === 1) return 'day';
  if (uniqueRates.length === 2) return rate === uniqueRates[0] ? 'cheap' : 'day';

  if (rate === uniqueRates[0]) return 'cheap';
  if (rate === uniqueRates[uniqueRates.length - 1]) return 'peak';
  return 'day';
};

export const processForecastForChart = (
  forecast: BehaviourForecastRecord[],
  costWindows: CostWindow[] = [],
  tz: string = SITE_TIMEZONE
): EnrichedForecastPoint[] => {
  if (!forecast?.length) return [];

  // Pre-sort once so the per-point rate lookup is O(n) not O(n log n) per call
  const sortedWindows = presortCostWindows(costWindows);

  return forecast.map((record, index) => {
    const nextRecord = forecast[index + 1];
    const prevRecord = index > 0 ? forecast[index - 1] : null;
    const tsSeconds = normalizeToUnixSeconds(record.target_datetime_utc);
    const rateWindow = tsSeconds !== null ? findRateInSortedWindows(tsSeconds, sortedWindows) : undefined;
    const rate = rateWindow?.rate ?? 0;
    const rateType = rateWindow?.rate_type ?? categoriseRate(rate, costWindows);
    const command: BatteryCommand = record.battery_update_command ?? 'hold';

    // optimal_target_soc_original is the END-of-interval target SoC (what the battery
    // aims to reach by the end of this interval, after the command has run).
    // To plot each marker at the START of its interval we use the previous record's
    // end SoC.  For the first forecast point there is no previous record; fall back to
    // the record's own SoC (the first command is normally a hold, so start ≈ end).
    const endSoc = Math.min(record.optimal_target_soc_original ?? 0, 100);
    const startSoc = prevRecord !== null ? Math.min(prevRecord.optimal_target_soc_original ?? 0, 100) : endSoc;

    return {
      x: formatTimeFromUtcTimestamp(record.target_datetime_utc, tz),
      y: startSoc,
      data: record,
      command,
      rateType,
      rate,
      startTime: formatTimeFromUtcTimestamp(record.target_datetime_utc, tz),
      endTime: nextRecord ? formatTimeFromUtcTimestamp(nextRecord.target_datetime_utc, tz) : '',
      activityType: deriveActivityType(command, rateType),
      _endSoc: endSoc,
    };
  });
};

export const calculateExpectedKwh = (
  currentSoc: number,
  previousSoc: number,
  batteryCapacityKwh?: number
): number | null => {
  if (!batteryCapacityKwh || batteryCapacityKwh <= 0) return null;
  return (Math.abs(currentSoc - previousSoc) / 100) * batteryCapacityKwh;
};

export const generateTooltipContent = (
  point: EnrichedForecastPoint,
  batteryCapacityKwh?: number,
  previousSoc?: number
): string => {
  const activityLabel = ACTIVITY_LABELS[point.activityType] || 'Battery idle';

  const rateLabels: Record<RateType, string> = {
    cheap: 'Cheap rate',
    day: 'Day rate',
    peak: 'Peak rate',
  };

  const timeRange = point.endTime ? `${point.startTime} to ${point.endTime}` : `from ${point.startTime}`;

  // Activity-specific first line.
  // For non-hold forecast points, _endSoc holds the target SoC reached at the end of the
  // interval ("charge to X%"), while point.y is the start-of-interval SoC used for plotting.
  // For historic points _endSoc is not set, and point.y is already the end-of-interval value.
  const displaySoc = point._endSoc ?? point.y;
  let firstLine: string;
  if (point.command === 'hold') {
    firstLine = `${activityLabel} at ${Math.round(point.y)}%`;
  } else {
    firstLine = `${activityLabel} to ${Math.round(displaySoc)}%`;
  }

  // kWh line (only for non-idle activities).
  // Forecast points carry _endSoc so we can derive the delta directly from the point.
  // Historic points fall back to the explicit previousSoc argument.
  let kwhLine = '';
  if (point.command !== 'hold') {
    const fromSoc = point._endSoc !== undefined ? point.y : previousSoc;
    const toSoc = point._endSoc !== undefined ? point._endSoc : point.y;
    if (fromSoc !== undefined) {
      const kwh = calculateExpectedKwh(toSoc, fromSoc, batteryCapacityKwh);
      if (kwh !== null && kwh > 0) {
        kwhLine = `\n~${kwh.toFixed(1)} kWh`;
      }
    }
  }

  // Tariff rate (only for grid interactions: grid charge and export)
  const isGridInteraction = point.activityType === 'gridCharge' || point.activityType === 'export';
  const rateLabel = rateLabels[point.rateType] || 'Day rate';
  const rateValue = point.rate > 0 ? `${point.rate.toFixed(1)}p/kWh` : '';
  const rateInfo = isGridInteraction && rateValue ? `\n${rateLabel}: ${rateValue}` : '';

  return `${firstLine}\n${timeRange}${kwhLine}${rateInfo}`;
};

/**
 * Minimal point shape required by buildRateShadingBlocksFromCostWindows.
 * EnrichedForecastPoint satisfies this, as does ShadingPoint from socChartDerivations.
 */
export interface ShadingCompatiblePoint {
  x: string;
  data: { target_datetime_utc: number };
}

/**
 * Builds tariff rate shading blocks (cheap and peak) from enriched forecast points
 * and a set of cost windows. Used to show import or export rate periods on the chart.
 *
 * @param points - Points with x and data.target_datetime_utc
 * @param costWindows - Cost windows (import or export tariff)
 * @returns Object with cheap and peak arrays of blocks; each block is array of { x, y, y0 }
 */
export const buildRateShadingBlocksFromCostWindows = (
  points: ShadingCompatiblePoint[],
  costWindows: CostWindow[]
): {
  cheap: Array<Array<{ x: string; y: number; y0: number }>>;
  peak: Array<Array<{ x: string; y: number; y0: number }>>;
} => {
  if (!points?.length) return { cheap: [], peak: [] };

  const sortedWindows = presortCostWindows(costWindows);

  const getRateType = (point: ShadingCompatiblePoint): RateType => {
    const tsSeconds = normalizeToUnixSeconds(point.data.target_datetime_utc);
    const rateWindow = tsSeconds !== null ? findRateInSortedWindows(tsSeconds, sortedWindows) : undefined;
    const rate = rateWindow?.rate ?? 0;
    return rateWindow?.rate_type ?? categoriseRate(rate, costWindows);
  };

  const blocks = {
    cheap: [] as Array<Array<{ x: string; y: number; y0: number }>>,
    peak: [] as Array<Array<{ x: string; y: number; y0: number }>>,
  };

  (['cheap', 'peak'] as const).forEach((targetRateType) => {
    let currentBlock: Array<{ x: string; y: number; y0: number }> = [];

    points.forEach((point, index) => {
      const rateType = getRateType(point);
      if (rateType === targetRateType) {
        currentBlock.push({ x: point.x, y: 100, y0: 0 });
      }

      const isEndOfBlock = rateType !== targetRateType || index === points.length - 1;
      if (isEndOfBlock && currentBlock.length > 0) {
        // For stepAfter interpolation: add the first non-matching point as a
        // closing boundary so the shading visually reaches the rate window edge
        // (e.g. cheap block at 02:00–04:30 extends to 05:00).
        if (rateType !== targetRateType) {
          currentBlock.push({ x: point.x, y: 100, y0: 0 });
        }
        if (currentBlock.length === 1) {
          currentBlock.push({ ...currentBlock[0] });
        }
        blocks[targetRateType].push(currentBlock);
        currentBlock = [];
      }
    });
  });

  return blocks;
};

export const findCommandTransitions = (points: EnrichedForecastPoint[]): EnrichedForecastPoint[] => {
  if (!points?.length || points.length < 2) return [];

  const transitions: EnrichedForecastPoint[] = [];

  for (let i = 1; i < points.length; i++) {
    if (points[i - 1].command !== points[i].command) {
      transitions.push(points[i]);
    }
  }

  return transitions;
};

export const getCommandColour = (command: BatteryCommand): string => {
  const colours: Record<BatteryCommand, string> = {
    charge: '#4CAF50',
    discharge: '#FF9800',
    hold: '#9E9E9E',
  };

  return colours[command] || colours.hold;
};
